import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, CheckCircle, ArrowRight, Play, MessageCircle } from "lucide-react";
import MedalPricingBanner from "../components/MedalPricingBanner";
import SEO from "../components/SEO";
import ShareBar from "../components/ShareBar";
import axios from "axios";
import { API } from "../config/constants";
import { MEDAL_VIDEO_SLOTS } from "../config/siteImages";

const MEDAL_AUDIO = "/assets/medals/l40.mp3";

export default function MedalsPage() {
  const audioRef = useRef(null);
  const [medalVideos, setMedalVideos] = useState(
    MEDAL_VIDEO_SLOTS.map((slot) => ({ src: slot.default, title: slot.title }))
  );
  const [highlight, setHighlight] = useState(null);

  useEffect(() => {
    axios.get(`${API}/medal-videos`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          setMedalVideos(res.data.map((v) => ({ src: v.url, title: v.title })));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!medalVideos.length) return;
    const match = window.location.hash.match(/#video-(\d+)/);
    if (!match) return;
    const idx = Number(match[1]);
    const el = document.getElementById(`video-${idx}`);
    if (!el) return;
    const timer = setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlight(idx);
      setTimeout(() => setHighlight(null), 2600);
    }, 200);
    return () => clearTimeout(timer);
  }, [medalVideos]);

  useEffect(() => {
    const audio = new Audio(MEDAL_AUDIO);
    audio.volume = 0.5;
    audioRef.current = audio;
    audio.play().catch(() => {});
    return () => { audio.pause(); audio.src = ""; };
  }, []);

  const steps = [
    { num: "01", title: "Share Your Vision", description: "Tell us about your event, athletes, and design ideas" },
    { num: "02", title: "Design & Approve", description: "Our team creates a custom design for your review" },
    { num: "03", title: "Production", description: "Skilled artisans craft your medals with precision" },
    { num: "04", title: "Delivery", description: "Receive your beautiful medals, ready to award" },
  ];

  const [playingVideo, setPlayingVideo] = useState(null);

  return (
    <div data-testid="medals-page" className="pt-20">
      <SEO title="Custom Medals" description="Premium custom medals for track & field events. Starting at $150 mold cost with 300 unit minimum. 45-day production from design approval." />
      <section className="py-12 md:py-16 bg-[#F8F9FA]">
        <div className="container-custom"><MedalPricingBanner /></div>
      </section>

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="section-kicker">Our Work</span>
            <h2 className="text-4xl font-bold text-[#0F2137]">Medal Gallery</h2>
            <p className="text-[#495057] mt-4 max-w-2xl mx-auto">Every medal tells a story. Watch our custom medal showcases from track & field events across the nation.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {medalVideos.map((video, index) => (
              <div key={video.title || `video-${index}`} id={`video-${index}`} className={`rounded-xl transition-all duration-500 ${highlight === index ? "ring-4 ring-[#D90429] ring-offset-2" : ""}`}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: Math.min(index * 0.1, 0.5) }}
                viewport={{ once: true, margin: "-50px" }}
                className="group relative overflow-hidden rounded-xl shadow-lg bg-[#0F2137] aspect-video cursor-pointer"
                onClick={() => setPlayingVideo(playingVideo === index ? null : index)}
                data-testid={`medal-video-${index}`}
              >
                <video
                  src={video.src}
                  className="w-full h-full object-cover"
                  loop muted playsInline
                  autoPlay={playingVideo === index}
                  ref={(el) => {
                    if (el) {
                      if (playingVideo === index) { el.play().catch(() => {}); }
                      else { el.pause(); el.currentTime = 0; }
                    }
                  }}
                />
                {playingVideo !== index && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity group-hover:bg-black/30">
                    <div className="w-16 h-16 bg-[#D90429] rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="text-white ml-1" size={28} fill="white" />
                    </div>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <p className="text-white font-bold text-lg">{video.title}</p>
                  <p className="text-white/70 text-sm">{playingVideo === index ? "Click to pause" : "Click to play"}</p>
                </div>
              </motion.div>
              <ShareBar url={`${typeof window !== "undefined" ? window.location.origin : "https://lane4.org"}/medals#video-${index}`} title={video.title} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="section-kicker">The Process</span>
            <h2 className="text-4xl font-bold text-[#0F2137]">How It Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div key={step.num} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} viewport={{ once: true }} className="relative">
                <span className="number-display text-6xl font-bold text-[#D90429]/20">{step.num}</span>
                <h3 className="text-xl font-bold text-[#0F2137] mt-2 mb-3">{step.title}</h3>
                <p className="text-[#495057]">{step.description}</p>
                {index < steps.length - 1 && <ChevronRight className="hidden lg:block absolute top-8 -right-4 text-[#D90429]" size={32} />}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="section-kicker">Our Promise</span>
            <h2 className="text-4xl font-bold text-[#0F2137] mb-6">Quality You Can Trust</h2>
            <p className="text-xl text-[#495057] mb-8">We understand the importance of quality craftsmanship and attention to detail. Our team of skilled artisans meticulously crafts each custom medal with precision and care, ensuring a beautiful and durable keepsake that will be treasured for years.</p>
            <div className="flex flex-wrap justify-center gap-4">
              {["Premium Materials", "Custom Designs", "Fast Turnaround", "Competitive Pricing"].map((item) => (
                <span key={item} className="flex items-center gap-2 bg-[#F8F9FA] px-4 py-2 rounded-full">
                  <CheckCircle className="text-[#D90429]" size={18} />{item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#D90429]">
        <div className="container-custom text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Create Your Custom Medals?</h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">Contact us today to discuss your vision and get a personalized quote.</p>
          <Link to="/contact" className="btn-outline-white inline-flex items-center gap-2">Request a Quote <ArrowRight size={20} /></Link>
        </div>
      </section>

      {/* Floating Quote Button - Mobile Only */}
      <Link
        to="/contact"
        data-testid="floating-quote-btn"
        className="fixed bottom-6 right-6 z-50 md:hidden bg-[#D90429] text-white rounded-full shadow-2xl flex items-center gap-2 px-5 py-3.5 hover:bg-[#B00322] transition-all hover:scale-105 active:scale-95"
      >
        <MessageCircle size={20} />
        <span className="font-semibold text-sm">Get a Quote</span>
      </Link>
    </div>
  );
}
